import React from 'react';
import { Helmet } from 'react-helmet-async';

// Column names from parseArticles are lowercased — these must match exactly
const SPORT_LABEL = {
  football:  'Football Intelligence',
  cricket:   'Cricket Intelligence',
  tennis:    'Tennis Intelligence',
  badminton: 'Badminton Intelligence',
  multi:     'Sports Intelligence',
};

function getSport(article) {
  if (!article) return 'football';
  return ((article['sport'] || article['category'] || 'football')).toLowerCase().trim();
}

function getContentType(article) {
  if (!article) return 'news';
  const ctx   = article['context'] || '';
  const parts = ctx.split('|');
  return parts.length >= 2 ? parts[1].trim().toLowerCase() : 'news';
}

const SEO = ({
  title,
  description,
  path  = '',
  url   = '',
  image,
  article,
}) => {
  const siteName     = 'Gametrait';
  const baseUrl      = 'https://gametrait.com';
  const defaultImage = `${baseUrl}/logo192.png`;
  const canonical    = url || `${baseUrl}${path}`;

  const fullTitle = title
    ? title.includes(siteName) ? title : `${title} | ${siteName}`
    : siteName;

  const ogType  = article ? 'article' : 'website';
  const ogImage = image || defaultImage;

  // Derive sport + content type for meta enrichment
  const sport       = getSport(article);
  const contentType = getContentType(article);
  const sportLabel  = SPORT_LABEL[sport] || 'Sports Intelligence';

  // Build keywords from sport + content type + title words
  const titleWords = (title || '').toLowerCase().replace(/[^\w\s]/g, '').split(/\s+/).filter((w) => w.length > 3);
  const keywords   = [...new Set([sport, contentType, 'gametrait', 'sports intelligence', ...titleWords.slice(0, 5)])].join(', ');

  // Article schema
  let articleSchema    = null;
  let publishedTimeISO = null;
  let articleSection   = sportLabel;

  if (article) {
    // parseArticles lowercases all column names — use lowercase keys
    const schemaTitle = article['title']         || title    || fullTitle;
    const schemaDesc  = article['short caption'] || description || '';
    const schemaImage = article['image url']     || ogImage;
    const schemaDateR = article['date']          || null;
    const schemaDate  = schemaDateR
      ? new Date(Number(schemaDateR) * 1000).toISOString()
      : new Date().toISOString();

    publishedTimeISO = schemaDate;
    articleSection   = SPORT_LABEL[sport] || sport;

    articleSchema = JSON.stringify({
      '@context': 'https://schema.org',
      '@type':    'NewsArticle',
      headline:    schemaTitle,
      description: schemaDesc,
      image:       schemaImage ? [schemaImage] : [],
      datePublished:  schemaDate,
      dateModified:   schemaDate,
      author: [{ '@type': 'Organization', name: siteName, url: baseUrl }],
      publisher: {
        '@type': 'Organization',
        name:    siteName,
        url:     baseUrl,
        logo: {
          '@type':  'ImageObject',
          url:      `${baseUrl}/logo192.png`,
          width:    192,
          height:   192,
        },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
      articleSection:   articleSection,
      keywords:         keywords,
      about: [{ '@type': 'Thing', name: sport.charAt(0).toUpperCase() + sport.slice(1) }],
    });
  }

  // Website schema for non-article pages
  const websiteSchema = !article ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type':    'WebSite',
    name:       siteName,
    url:        baseUrl,
    description: 'AI-generated sports intelligence across football, cricket, tennis and badminton.',
    potentialAction: {
      '@type':       'SearchAction',
      target:        `${baseUrl}/feed?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }) : null;

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description"           content={description || `${siteName} — ${sportLabel}`} />
      <meta name="keywords"              content={keywords} />
      <link rel="canonical"              href={canonical} />

      {/* Open Graph */}
      <meta property="og:type"           content={ogType} />
      <meta property="og:title"          content={fullTitle} />
      <meta property="og:description"    content={description || ''} />
      <meta property="og:url"            content={canonical} />
      <meta property="og:site_name"      content={siteName} />
      <meta property="og:image"          content={ogImage} />
      <meta property="og:image:width"    content="1200" />
      <meta property="og:image:height"   content="630" />

      {/* Article-specific OG */}
      {article && publishedTimeISO && (
        <meta property="article:published_time" content={publishedTimeISO} />
      )}
      {article && (
        <meta property="article:section" content={articleSection} />
      )}
      {article && sport && (
        <meta property="article:tag" content={sport} />
      )}
      {article && contentType && contentType !== 'news' && (
        <meta property="article:tag" content={contentType} />
      )}

      {/* Twitter */}
      <meta name="twitter:card"          content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title"         content={fullTitle} />
      <meta name="twitter:description"   content={description || ''} />
      <meta name="twitter:image"         content={ogImage} />
      <meta name="twitter:site"          content="@gametrait" />

      {/* iOS App banner */}
      {canonical.includes('/feed/') && (
        <meta name="apple-itunes-app" content={`app-id=6742704573, app-argument=${canonical}`} />
      )}

      {/* JSON-LD */}
      {articleSchema && (
        <script type="application/ld+json">{articleSchema}</script>
      )}
      {websiteSchema && (
        <script type="application/ld+json">{websiteSchema}</script>
      )}
    </Helmet>
  );
};

export default SEO;